import React from 'react';
import type { PdfTemplateHeader } from '../../types/pdfTemplate';

interface PdfHeaderSettingsProps {
  header: PdfTemplateHeader;
  onChange: (header: PdfTemplateHeader) => void;
}

const PdfHeaderSettings: React.FC<PdfHeaderSettingsProps> = ({ header, onChange }) => {
  const update = (field: keyof PdfTemplateHeader, value: string | boolean) => {
    onChange({ ...header, [field]: value });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-900">Header Settings</h3>

      {/* Logo toggle */}
      <div className="flex items-center space-x-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={header.showLogo}
            onChange={e => update('showLogo', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-brand-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
        </label>
        <span className="text-sm text-gray-700">Show logo/company name</span>
      </div>

      {/* Logo Position */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Logo Position</label>
        <select
          value={header.logoPosition}
          onChange={e => update('logoPosition', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>

      {/* Company Name */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Company Name</label>
        <input
          type="text"
          value={header.companyName || ''}
          onChange={e => update('companyName', e.target.value)}
          placeholder="Your Company Name"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      {/* Tagline */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Tagline</label>
        <input
          type="text"
          value={header.tagline || ''}
          onChange={e => update('tagline', e.target.value)}
          placeholder="Building Excellence Since 2024"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      {/* Address */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Address</label>
        <input
          type="text"
          value={header.address || ''}
          onChange={e => update('address', e.target.value)}
          placeholder="123 Main St, City, State 12345"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      {/* Phone & Email */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Phone</label>
          <input
            type="text"
            value={header.phone || ''}
            onChange={e => update('phone', e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Email</label>
          <input
            type="email"
            value={header.email || ''}
            onChange={e => update('email', e.target.value)}
            placeholder="info@company.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </div>

      {/* Website */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Website</label>
        <input
          type="url"
          value={header.website || ''}
          onChange={e => update('website', e.target.value)}
          placeholder="https://www.company.com"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      {/* Logo URL */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Logo URL (S3)</label>
        <input
          type="url"
          value={header.logoUrl || ''}
          onChange={e => update('logoUrl', e.target.value)}
          placeholder="https://s3.amazonaws.com/your-bucket/logo.png"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      {/* Header Color */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Header Color</label>
        <div className="flex items-center space-x-3">
          <input
            type="color"
            value={header.headerColor}
            onChange={e => update('headerColor', e.target.value)}
            className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
          />
          <input
            type="text"
            value={header.headerColor}
            onChange={e => update('headerColor', e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 font-mono"
          />
        </div>
      </div>
    </div>
  );
};

export default PdfHeaderSettings;
